import "./style.css";
import { Store } from "./state/store";
import { AudioEngine } from "./audio/engine";
import { RecorderManager, listInputs, listOutputs, InputDevice, MicProcessing, REMOTE_INPUT, createCapture } from "./audio/recorder";
import { Timeline } from "./ui/timeline";
import { Mixer } from "./ui/mixer";
import { openModal, toast } from "./ui/modal";
import { openFxEditor } from "./ui/fx-editor";
import { openClipInspector } from "./ui/clip-inspector";
import { openPianoRoll } from "./ui/piano-roll";
import { openSequencer } from "./ui/sequencer";
import { el, fmtTime, fmtBars, uid } from "./util";
import { newTrack, newClip, newMidiClip, Effect, RecordMode, RECORD_MODE_LABELS } from "./state/model";
import { makeEffect, EFFECT_LABELS, DENOISE_WORKLET_URL, DEVERB_WORKLET_URL } from "./audio/effects";
import { saveProject, loadProject, listProjects, deleteProject } from "./state/storage";
import { renderMixdown } from "./io/render";
import { encodeWav, downloadBlob } from "./io/export-wav";
import { exportAac, aacSupported } from "./io/export-aac";
import { exportDawproject, importDawproject } from "./io/dawproject";
import { exportStems } from "./io/export-stems";
import { loadWamEffect, openWamGui, reconcileWam } from "./audio/wam";
import { WAM_EFFECTS } from "./audio/wam-presets";
import { calibrateLatency } from "./audio/calibrate";
import { RemoteSession, jbBetween } from "./sync/rtc";
import { MidiInput } from "./audio/midi";

const store = new Store();
const engine = new AudioEngine(store);
const recorder = new RecorderManager(engine);
const midi = new MidiInput(store, engine);
let devices: InputDevice[] = [];
let recordStartPos = 0;
let lastArmSig = "";
// Manual record-latency offset (ms), device-specific → persisted in localStorage.
let recOffsetMs = Number(localStorage.getItem("webdaw.recOffsetMs") || 0);
const remote = new RemoteSession(store, engine);

// ---- layout ----------------------------------------------------------
const app = el("div", { class: "app" });
const topbar = el("div", { class: "topbar" });
const main = el("div", { class: "main" });
app.append(topbar, main);
document.getElementById("app")!.append(app);

const timeline = new Timeline(store, engine, {
  // MIDI clips open the piano roll; audio clips open the clip inspector.
  editClip: (clipId) => { const f = store.findClip(clipId); if (f?.clip.notes) openPianoRoll(store, engine, clipId); else openClipInspector(store, engine, clipId); },
  dropAudio: (files, trackId, startSec) => { void dropAudioFiles(files, trackId, startSec); },
});
const mixer = new Mixer(store, engine, {
  openFx: (target, fxId) => openFxEditor(store, engine, target, fxId, (id) => guiForWam(target, id)),
  addFx: (target) => addFxPicker(target),
});
main.append(timeline.el, mixer.el);
timeline.mount();

// ---- top bar ---------------------------------------------------------
const brand = el("div", {
  class: "brand",
  attrs: { title: "WebDAW — multitrack studio" },
  html: `
    <svg class="brand-mark" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="wdmark" x1="2" y1="2" x2="30" y2="30" gradientUnits="userSpaceOnUse">
          <stop stop-color="#6ea0ff"/><stop offset="1" stop-color="#9b6bff"/>
        </linearGradient>
      </defs>
      <rect x="1.25" y="1.25" width="29.5" height="29.5" rx="8.5" fill="#131826" stroke="url(#wdmark)" stroke-opacity=".55" stroke-width="1.5"/>
      <g fill="url(#wdmark)">
        <rect x="6"  y="13" width="3" height="6"  rx="1.5"/>
        <rect x="11" y="9.5" width="3" height="13" rx="1.5"/>
        <rect x="16" y="5"  width="3" height="22" rx="1.5"/>
        <rect x="21" y="10" width="3" height="12" rx="1.5"/>
        <rect x="26" y="14" width="3" height="4"  rx="1.5"/>
      </g>
    </svg>
    <span class="brand-text">
      <span class="brand-name"><span class="web">Web</span><span class="daw">DAW</span></span>
      <span class="brand-tag">Multitrack Studio</span>
    </span>`,
});

const projName = el("input", { attrs: { title: "Project name" } }) as HTMLInputElement;
projName.value = store.project.name;
projName.style.width = "150px";
projName.addEventListener("change", () => { store.project.name = projName.value; store.emitChange("project"); });

const fileBtn = btn("File ▾", openFileMenu);
const importBtn = btn("Import ▾", openImportMenu);
const exportBtn = btn("Export ▾", openExportMenu);

// transport
const tBack = btn("⏮", () => engine.seek(0), "Return to start (Home)");
const tPlay = btn("▶", togglePlay, "Play / pause (Space)");
const tStop = btn("⏹", stopTransport, "Stop");
const tRec = btn("⏺", toggleRecord, "Record (R)");
tRec.classList.add("rec");
const transportGroup = el("div", { class: "group transport" }, [tBack, tPlay, tStop, tRec]);

const timecode = el("div", { class: "timecode" });
const tempoIn = el("input", { attrs: { type: "number", min: "40", max: "300", step: "1" } }) as HTMLInputElement;
tempoIn.value = String(store.project.tempo);
tempoIn.addEventListener("change", () => { store.setTempo(Number(tempoIn.value) || 120); tempoIn.value = String(store.project.tempo); });
const tempoGroup = el("div", { class: "group tempo" }, [el("span", { class: "hint", text: "BPM" }), tempoIn]);

const metroBtn = btn("🅼", () => { engine.setMetronome(!engine.metronome); metroBtn.classList.toggle("on", engine.metronome); }, "Metronome");
metroBtn.classList.add("toggle");

// Count-in (pre-count): play one bar of clicks before recording starts.
let countInOn = localStorage.getItem("webdaw.countIn") === "1";
let countingIn = false, countInAbort = false;
const countInBtn = btn("⏱1", () => { countInOn = !countInOn; countInBtn.classList.toggle("on", countInOn); localStorage.setItem("webdaw.countIn", countInOn ? "1" : "0"); });
countInBtn.classList.add("toggle"); countInBtn.classList.toggle("on", countInOn);
countInBtn.title = "Count-in — play one bar of metronome clicks before recording";
const loopBtn = btn("🔁", () => {
  store.project.loopEnabled = !store.project.loopEnabled;
  store.emitChange("transport");
  engine.refreshLoop(); // apply immediately if already playing
}, "Loop — repeats the whole song (to the end of its last bar), or the loop region if set. Shift-drag the ruler to set a region; right-click the ruler to clear it.");
loopBtn.classList.add("toggle"); loopBtn.classList.toggle("on", store.project.loopEnabled);

const addAudioBtn = btn("+ Audio", () => store.addTrack(newTrack(store.project.tracks.length, "audio")));
const addInstBtn = btn("+ Inst", () => {
  const t = newTrack(store.project.tracks.length, "instrument");
  store.addTrack(t);
  store.select(t.id, null);          // select so it's the default MIDI target
  midi.ensureWebMidi();              // a MIDI feature is now in use → request controller access
});
const delTrackBtn = btn("− Track", () => openDeleteTracksDialog());

// Pick which track(s) to delete before removing anything (no silent delete).
function openDeleteTracksDialog() {
  const tracks = store.project.tracks;
  if (!tracks.length) { toast("No tracks to delete."); return; }
  const checks = new Map<string, HTMLInputElement>();
  const list = el("div", { class: "trk-del-list" });
  for (const t of tracks) {
    const cb = el("input", { attrs: { type: "checkbox" } }) as HTMLInputElement;
    if (t.id === store.selectedTrackId) cb.checked = true; // default: the selected track
    checks.set(t.id, cb);
    const sw = el("span", { class: "trk-del-swatch" }); sw.style.background = t.color;
    const meta = t.kind === "instrument" ? "MIDI" : "audio";
    const clips = t.clips.length ? `${t.clips.length} clip${t.clips.length > 1 ? "s" : ""}` : "empty";
    const row = el("label", { class: "trk-del-row" }, [cb, sw, el("span", { class: "trk-del-name", text: t.name }), el("span", { class: "trk-del-meta hint", text: `${meta} · ${clips}` })]);
    list.append(row);
  }
  const body = el("div", { class: "trk-del" }, [el("p", { class: "hint", text: "Select the track(s) to delete. This can be undone (Ctrl+Z)." }), list]);
  openModal("Delete tracks", body, [
    { label: "Cancel", onClick: () => {} },
    { label: "Delete selected", primary: true, onClick: () => {
      const ids = [...checks].filter(([, cb]) => cb.checked).map(([id]) => id);
      if (!ids.length) { toast("No tracks selected."); return false; } // keep dialog open
      store.removeTracks(ids);
      toast(`Deleted ${ids.length} track${ids.length > 1 ? "s" : ""}.`);
    } },
  ]);
}
const seqBtn = btn("▦ Sequencer", () => openSequencer(store, engine));
seqBtn.title = "Step sequencer / beat-maker — build a pattern and add it to the timeline";

// musical-typing toggle: play the armed/selected instrument track from the QWERTY keyboard
const keysBtn = btn("🎹 Keys", () => { midi.ensureWebMidi(); midi.setTyping(!midi.typing); });
keysBtn.classList.add("toggle");
midi.onState = () => {
  keysBtn.classList.toggle("on", midi.typing);
  keysBtn.title = midi.typing ? `Musical typing ON — A–K play notes, Z/X change octave (oct ${midi.octave >= 0 ? "+" : ""}${midi.octave})` : "Play instrument tracks from your computer keyboard";
};
midi.onState();

const undoBtn = btn("↶", () => store.undo(), "Undo (Ctrl+Z)");
const redoBtn = btn("↷", () => store.redo(), "Redo (Ctrl+Y)");

const zoomSlider = el("input", { attrs: { type: "range", min: "16", max: "400", step: "2", value: String(timeline.pps), title: "Zoom" } }) as HTMLInputElement;
zoomSlider.style.width = "110px";
zoomSlider.addEventListener("input", () => timeline.setZoom(Number(zoomSlider.value)));

// vertical zoom: global track row height
const trackHSlider = el("input", { attrs: { type: "range", min: "56", max: "400", step: "4", value: String(timeline.trackHeight), title: "Track height" } }) as HTMLInputElement;
trackHSlider.style.width = "90px";
trackHSlider.addEventListener("input", () => timeline.setTrackHeight(Number(trackHSlider.value)));

const snapBtn = btn("⌗ Snap", () => { timeline.snap = !timeline.snap; snapBtn.classList.toggle("on", timeline.snap); });
snapBtn.classList.add("toggle"); snapBtn.classList.toggle("on", timeline.snap);
snapBtn.title = "Snap clips to the beat grid when moving/trimming (hold Alt to invert)";

const recModeSel = el("select", { attrs: { title: "Record mode: how a new take interacts with existing audio on a track (project-wide)" } }) as HTMLSelectElement;
for (const m of ["comp", "sos", "overwrite"] as RecordMode[]) {
  const o = el("option", { text: RECORD_MODE_LABELS[m], attrs: { value: m } }) as HTMLOptionElement;
  if ((store.project.recordMode ?? "comp") === m) o.selected = true;
  recModeSel.append(o);
}
recModeSel.value = store.project.recordMode ?? "comp";
recModeSel.addEventListener("change", () => { store.project.recordMode = recModeSel.value as RecordMode; store.emitChange("project"); });
const recModeGroup = el("div", { class: "group" }, [el("span", { class: "hint", text: "Rec" }), recModeSel]);

// Mic processing: Raw (studio, default) vs Noise-reduced. Chrome on Windows
// captures in WASAPI raw mode, which skips the driver/OS noise suppression and
// mic-array processing other DAWs on the default Windows path get for free.
const micProcSel = el("select", { attrs: { title: "Mic processing — Raw: unprocessed studio capture (bypasses Windows/driver noise suppression). Noise-reduced: applies noise suppression (Windows Voice Focus where supported, else the browser's) for less room noise." } }) as HTMLSelectElement;
for (const [v, label] of [["nr", "Noise-reduced"], ["raw", "Raw"]] as const) micProcSel.append(el("option", { text: label, attrs: { value: v } }));
const savedProc = (() => { try { return localStorage.getItem("webdaw.micProcessing"); } catch { return null; } })();
micProcSel.value = savedProc === "raw" ? "raw" : "nr"; // Noise-reduced by default; Raw only if explicitly chosen
recorder.processing = micProcSel.value as MicProcessing;
micProcSel.addEventListener("change", () => {
  try { localStorage.setItem("webdaw.micProcessing", micProcSel.value); } catch {}
  recorder.processing = micProcSel.value as MicProcessing;
  recorder.disposeAll(); recorder.releaseStreams(); // reopen the mic with the new constraints
  lastArmSig = ""; void refreshArm();
  toast(micProcSel.value === "nr" ? "Mic: noise-reduced capture." : "Mic: raw capture.");
});
const micProcGroup = el("div", { class: "group" }, [el("span", { class: "hint", text: "Mic" }), micProcSel]);

const recOffsetIn = el("input", { attrs: { type: "number", step: "5", title: "Recording latency offset (ms). Increase if recorded takes land late (behind the beat); decrease if early." } }) as HTMLInputElement;
recOffsetIn.style.width = "54px";
recOffsetIn.value = String(recOffsetMs);
recOffsetIn.addEventListener("change", () => { setRecOffset(Number(recOffsetIn.value) || 0); });
const calibBtn = btn("⇄ Calibrate", runCalibration);
calibBtn.title = "Auto-measure recording latency: plays clicks through your speakers and records them back. Use speakers (not headphones).";
const recOffsetGroup = el("div", { class: "group" }, [el("span", { class: "hint", text: "Latency ms" }), recOffsetIn, calibBtn]);

// Devices chosen in the failure dialog; default (null) = system default.
let calibInputId: string | null = null;
let calibOutputId: string | null = null;

async function runCalibration() {
  try {
    if (engine.playing || engine.recording) engine.stop();
    await engine.resume();
    await engine.ctx.audioWorklet.addModule("/recorder-worklet.js").catch(() => {});
    toast("Calibrating… you'll hear 3 beeps — keep quiet, use speakers (not headphones).", 4000);
    const r = await calibrateLatency(engine.ctx, { inputDeviceId: calibInputId, outputDeviceId: calibOutputId });
    if ("error" in r) { showCalibrationDevicePicker(r.error); return; }
    const auto = (engine.ctx.outputLatency || engine.ctx.baseLatency || 0) + recorder.inputLatency();
    setRecOffset(Math.max(0, Math.round(r.roundTripMs - auto * 1000)));
    toast(`Heard ${r.samples}/3 clicks on “${r.inputLabel}” (round-trip ${r.roundTripMs} ms) → latency offset set to ${recOffsetMs} ms.`, 7000);
  } catch (e: any) {
    console.error("calibration failed", e);
    showCalibrationDevicePicker("Calibration failed: " + (e?.message || e));
  }
}

/** Calibration couldn't hear the clicks — the default mic/output may be wrong
 *  (e.g. listening on a headset mic, or playing to headphones). Let the user
 *  pick the input and output to use, then retry. */
async function showCalibrationDevicePicker(errMsg: string) {
  let inputs: InputDevice[] = [], outputs: InputDevice[] = [];
  try { inputs = await listInputs(); } catch {}
  try { outputs = await listOutputs(); } catch {}

  const mk = (devs: InputDevice[], current: string | null) => {
    const sel = el("select") as HTMLSelectElement;
    sel.append(el("option", { text: "System default", attrs: { value: "" } }));
    for (const d of devs) {
      const o = el("option", { text: d.label, attrs: { value: d.deviceId } }) as HTMLOptionElement;
      if (d.deviceId === current) o.selected = true;
      sel.append(o);
    }
    return sel;
  };
  const labeled = (label: string, ctrl: HTMLElement) => el("div", { class: "fx-param" }, [el("label", { text: label }), ctrl]);
  const inSel = mk(inputs, calibInputId);
  const body = el("div", { class: "fx-editor" }, [
    el("p", { text: errMsg }),
    el("p", { class: "hint", text: "Pick the mic that can hear your speakers and the speakers to play from, then retry. Use real speakers — headphones won't reach the mic." }),
    labeled("Microphone", inSel),
  ]);
  let outSel: HTMLSelectElement | null = null;
  if (outputs.length && typeof (engine.ctx as any).setSinkId === "function") {
    outSel = mk(outputs, calibOutputId);
    body.append(labeled("Output", outSel));
  }
  openModal("Choose calibration devices", body, [
    { label: "Cancel", onClick: () => {} },
    { label: "⇄ Retry calibration", primary: true, onClick: () => {
      calibInputId = inSel.value || null;
      if (outSel) calibOutputId = outSel.value || null;
      runCalibration();
    } },
  ]);
}

const syncBtn = btn("⇄ Remote", openRemoteMenu);
const syncBadge = el("span", { class: "badge", text: "offline" });

topbar.append(
  brand, projName, fileBtn, importBtn, exportBtn,
  sep(), transportGroup, timecode, tempoGroup,
  sep(), metroBtn, countInBtn, loopBtn, snapBtn, recModeGroup, micProcGroup, recOffsetGroup,
  sep(), undoBtn, redoBtn,
  sep(), addAudioBtn, addInstBtn, delTrackBtn, keysBtn, seqBtn,
  sep(), el("span", { class: "hint", text: "Zoom" }), zoomSlider,
  el("span", { class: "hint", text: "Track H" }), trackHSlider,
  el("div", { class: "spacer" }),
  syncBtn, syncBadge,
);

function btn(label: string, on: () => void, title?: string): HTMLButtonElement {
  const b = el("button", { text: label }); b.addEventListener("click", on);
  if (title) { b.title = title; b.setAttribute("aria-label", title); }
  // Don't take keyboard focus on a mouse click: a focused button would also
  // "click" on Space, so Space-to-play would toggle twice. Tab still focuses it.
  b.addEventListener("mousedown", (e) => e.preventDefault());
  return b;
}
function sep() { return el("div", { class: "sep" }); }

// ---- transport / record ---------------------------------------------
async function togglePlay() {
  if (engine.recording) { await finishRecord(); return; } // pause/space during record = finalize take
  await engine.resume();
  if (engine.playing) engine.pause(); else engine.play();
}

async function stopTransport() {
  if (countingIn) { countInAbort = true; engine.cancelCountIn(); }
  if (engine.recording) await finishRecord(); // materialize the take before resetting
  engine.stop();
}

/** Input choices for audio tracks: local devices, plus the partner's mic while
 *  they've allowed recording in the current remote session. */
function pushDevices() {
  const remoteOk = remote.remoteGrant === "granted";
  timeline.setDevices(remoteOk ? [...devices, { deviceId: REMOTE_INPUT, label: "🌐 Partner's mic (remote)" } as InputDevice] : devices);
}

// Audio-armed tracks only — instrument tracks record MIDI and need no mic input.
function armedTracks() {
  return store.project.tracks.filter((t) => t.armed && t.kind !== "instrument").map((t) => ({ id: t.id, deviceId: t.inputDeviceId, monitor: t.monitor, inputGain: t.inputGain ?? 0, inputChannel: t.inputChannel }));
}

function micError(e: any): string {
  const name = e?.name || "";
  if (name === "RemotePermissionError") return `${e.message} Ask for permission in ⇄ Remote → Recording.`;
  if (name === "NotAllowedError" || name === "SecurityError") return "Microphone blocked. Allow mic access in your browser's site settings (the 🎤/🔒 icon in the address bar), then try again.";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "No microphone found for the selected input. Pick a different input device on the track.";
  if (name === "NotReadableError") return "Microphone is in use by another app. Close it and try again.";
  return `Could not access the microphone (${name || "unknown error"}).`;
}

async function refreshArm() {
  const armed = armedTracks();
  const sig = armed.map((a) => `${a.id}:${a.deviceId}:${a.monitor}:${a.inputChannel ?? "1"}`).join("|");
  if (sig === lastArmSig) return;
  lastArmSig = sig;
  if (armed.length === 0) { recorder.disposeAll(); recorder.releaseStreams(); return; }
  // Pre-arm for input monitoring/metering. Failures here are non-fatal; the real
  // gate is at record time, so keep this quiet (no scary toast while just arming).
  try { await recorder.arm(armed); }
  catch (e) { console.warn("pre-arm failed (will retry at record):", e); lastArmSig = ""; }
}

let warnedInputDsp = false;
function warnIfInputProcessed() {
  if (warnedInputDsp) return;
  const p = recorder.inputProcessing();
  if (p && (p.agc || (p.ns && recorder.processing === "raw"))) {
    warnedInputDsp = true;
    toast("Heads up: your mic's auto-gain/noise-suppression is on (forced by the OS/driver) — it limits dynamics. Disable mic 'enhancements' in your system sound settings for full-range recordings.", 9000);
  }
}

// A typical built-in-audio round-trip; calibration finds the exact value, but
// this gets a first-time user roughly on the beat without the extra step.
const DEFAULT_LATENCY_MS = 40;
function setRecOffset(ms: number) { recOffsetMs = ms; recOffsetIn.value = String(ms); localStorage.setItem("webdaw.recOffsetMs", String(ms)); }
// "Configured" = the user has calibrated, typed an offset, or seen+dismissed
// the suggestion. We only nudge a brand-new user who hasn't done any of these.
function latencyConfigured(): boolean { return localStorage.getItem("webdaw.recOffsetMs") !== null || localStorage.getItem("webdaw.latencyAcked") === "1"; }
function ackLatency() { localStorage.setItem("webdaw.latencyAcked", "1"); }

/** First-time nudge: suggest calibrating (or a sensible default) before the
 *  very first recording, so takes land on the beat instead of drifting late. */
function suggestLatencyCalibration() {
  const body = el("div", {}, [
    el("p", { text: "Recording latency isn't set yet. Your mic/interface adds a round-trip delay, so takes recorded against existing tracks can land slightly late unless it's compensated." }),
    el("p", { class: "hint", text: `Calibrate measures it exactly (plays clicks through your speakers and records them back). Or apply a typical ~${DEFAULT_LATENCY_MS} ms default now and fine-tune later with the “Latency ms” box.` }),
  ]);
  openModal("Set recording latency first", body, [
    { label: "⇄ Calibrate now", primary: true, onClick: () => { ackLatency(); runCalibration(); } },
    { label: `Use ~${DEFAULT_LATENCY_MS} ms default`, onClick: () => { setRecOffset(DEFAULT_LATENCY_MS); ackLatency(); toast(`Latency offset set to ${DEFAULT_LATENCY_MS} ms — calibrate later for an exact value.`, 5000); beginRecording(); } },
    { label: "Record without it", onClick: () => { ackLatency(); beginRecording(); } },
  ]);
}

async function toggleRecord() {
  if (countingIn) { countInAbort = true; engine.cancelCountIn(); toast("Count-in canceled.", 1500); return; }
  if (engine.recording) { await finishRecord(); return; }
  const armed = store.project.tracks.filter((t) => t.armed);
  if (armed.length === 0) { toast("Arm a track first — click the red ● (R) on the track header."); return; }
  // Mic-specific gates only apply when an *audio* track is armed; MIDI/instrument
  // recording needs no microphone, secure context, or latency calibration.
  const hasAudio = armed.some((t) => t.kind !== "instrument");
  if (hasAudio) {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      toast("Recording needs a secure context. Open the app via http://localhost:5174 (not a LAN IP) or use https.", 8000);
      return;
    }
    if (recOffsetMs === 0 && !latencyConfigured()) { suggestLatencyCalibration(); return; }
  }
  await beginRecording();
}

async function beginRecording() {
  const armed = store.project.tracks.filter((t) => t.armed);
  if (armed.length === 0) return;
  await engine.resume();
  const armedAudio = armed.filter((t) => t.kind !== "instrument")
    .map((t) => ({ id: t.id, deviceId: t.inputDeviceId, monitor: t.monitor, inputGain: t.inputGain ?? 0, inputChannel: t.inputChannel }));
  if (armedAudio.length) {
    // (Re)arm the mic fresh and surface a clear error if it can't be opened.
    try {
      await recorder.arm(armedAudio);
    } catch (e) {
      console.error("record arm failed:", e);
      toast(micError(e), 7000);
      return;
    }
    if (recorder.count() === 0) { toast("No usable microphone input.", 6000); return; }
    warnIfInputProcessed();
  }
  // Optional count-in: play one bar of clicks before capture actually begins.
  if (countInOn) {
    countingIn = true; countInAbort = false;
    tRec.classList.add("on");
    const bpb = store.project.beatsPerBar;
    const spb = 60 / store.project.tempo;
    for (let i = 0; i < bpb; i++) setTimeout(() => { if (countingIn) toast(`Count-in ${i + 1}/${bpb}`, spb * 1000 + 300); }, 120 + i * spb * 1000);
    await engine.countIn(bpb);
    countingIn = false;
    if (countInAbort) { tRec.classList.remove("on"); return; } // stop/record pressed during count-in
  }
  const from = engine.position();
  engine.recording = true;
  tRec.classList.add("on");
  // Wait until playback is really scheduled: play() is async, and starting the
  // capture before it put every audio take ~60 ms late (the transport's start lead).
  await engine.play(from);
  recordAnchor = engine.anchor();
  // Timeline position at the instant capture starts (audio takes are refined at
  // finish from the capture's own first-sample time); latency is compensated then.
  recordStartPos = engine.position();
  midiStartPos = recordStartPos; // MIDI notes are stored relative to this
  if (armedAudio.length) recorder.begin();
  midi.startRecording(recordStartPos); // captures notes for armed instrument tracks (no-op if none)
  // Recording the partner's mic: let them know (they see a banner while it runs),
  // and start measuring the network delay their audio will arrive with.
  partnerTrackIds = new Set(armedAudio.filter((a) => a.deviceId === REMOTE_INPUT).map((a) => a.id));
  recordingPartner = partnerTrackIds.size > 0;
  partnerStatsStart = null;
  if (recordingPartner) {
    remote.notifyRecordingPeer(true);
    remote.requestLatency();
    void remote.delayStats().then((s) => { partnerStatsStart = s; });
    // Their app records its mic too (exact timing, full quality) and sends it at stop.
    const anchor = recordAnchor;
    partnerTake = anchor ? { takeId: uid("ptk"), anchor } : null;
    if (partnerTake) remote.startPartnerTake(partnerTake.takeId);
  }
  toast(recordingPartner ? "● Recording — including your partner's mic (they can see this)." : "● Recording…", 2500);
}
let recordingPartner = false;
/** Where the timeline was on the audio clock when this recording started. */
let recordAnchor: { pos: number; at: number } | null = null;
let midiStartPos = 0;
let partnerTrackIds = new Set<string>();
let partnerStatsStart: Awaited<ReturnType<typeof remote.delayStats>> = null;
let lastPartnerComp: PartnerComp | null = null;
/** The take being recorded from the partner: id + where our timeline was on our audio clock. */
let partnerTake: { takeId: string; anchor: { pos: number; at: number } } | null = null;
/** Network takes waiting for the partner's full-quality version. */
const pendingPartnerTakes = new Map<string, { anchor: { pos: number; at: number }; clips: { clipId: string; placed: { start: number; offset: number; duration: number } }[] }>();

/** Fine-tune for partner recordings (ms, + = slide earlier), kept per browser. */
let partnerOffsetMs = Number(localStorage.getItem("webcut.partnerOffsetMs") || 0);
// Used only when the audio path couldn't be measured with a probe:
const OPUS_DELAY = 0.03;     // Opus 20 ms frame + lookahead + packetizing (typical)
const BROWSER_BUFFER = 0.09; // browser audio buffering in and out of the call (not in WebRTC stats)
interface PartnerComp { total: number; transport: number; partner: number; path: number; manual: number; pathMeasured: boolean; rtt: number; jitter: number; }

/** How late the partner's voice lands in our recording, in seconds:
 *    transport — their playback runs behind ours (≈0 once the clocks are synced,
 *                since Play/Record starts them in step; else ½ RTT)
 *  + partner   — their own audio round trip: hearing the playback + their mic
 *                (their calibration, reported by their app)
 *  + path      — their audio → network → jitter buffer → our recorder: measured
 *                with a probe chirp and adjusted for the jitter buffer during the
 *                take; estimated from WebRTC stats when no probe is available
 *  + manual    — the fine-tune in ⇄ Remote. */
function partnerCompensation(end: Awaited<ReturnType<typeof remote.delayStats>>, start = partnerStatsStart): PartnerComp {
  const rtt = end?.rtt ?? start?.rtt ?? 0.1;
  const jitter = jbBetween(start, end) ?? 0.04;
  const transport = remote.clockOffset != null ? 0 : rtt / 2;
  const partner = remote.partnerLatency ?? 0.05; // they didn't report → a typical device
  const pathMeasured = remote.pipelineDelay != null;
  const path = pathMeasured
    ? remote.pipelineDelay! + (jitter - (remote.pipelineJb ?? jitter)) // measured, tracking jitter-buffer changes
    : rtt / 2 + jitter + OPUS_DELAY + BROWSER_BUFFER;
  const manual = partnerOffsetMs / 1000;
  const total = Math.max(0, transport + partner + path + manual);
  return { total, transport, partner, path, manual, pathMeasured, rtt, jitter };
}
const compText = (c: PartnerComp) =>
  `${c.transport ? `playback sync ${ms(c.transport)} + ` : ""}their audio ${ms(c.partner)}${remote.partnerLatency == null ? " (estimated)" : ""} + audio path ${ms(c.path)} (${c.pathMeasured ? "measured" : "estimated"})${c.manual ? ` + fine-tune ${ms(c.manual)}` : ""}`;
const ms = (s: number) => `${Math.round(s * 1000)} ms`;


/** Round-trip latency to shift recorded takes earlier so they sit on the beat:
 *  output latency + (best-effort) input latency + the user's manual offset. */
function recordCompensation(): number {
  const out = engine.ctx.outputLatency || engine.ctx.baseLatency || 0;
  return out + recorder.inputLatency() + recOffsetMs / 1000;
}

async function finishRecord() {
  engine.recording = false;
  tRec.classList.remove("on");
  const midiByTrack = midi.stopRecording(); // capture MIDI before the transport clock stops
  const partnerTracks = partnerTrackIds;
  let pComp: PartnerComp | null = null;
  if (recordingPartner) {
    recordingPartner = false; remote.notifyRecordingPeer(false);
    pComp = partnerCompensation(await remote.delayStats());
    lastPartnerComp = pComp;
  }
  partnerTrackIds = new Set();
  const take = partnerTake; partnerTake = null;
  // Exact placement: the capture's first sample (audio clock) → timeline.
  const first = recorder.firstSampleTime();
  if (first != null && recordAnchor) recordStartPos = recordAnchor.pos + (first - recordAnchor.at);
  recordAnchor = null;
  const captured = await recorder.finish();
  engine.pause();
  const mode = store.project.recordMode ?? "comp"; // project-wide record mode
  const localComp = recordCompensation(); // shift take earlier to align with playback
  for (const [trackId, buf] of captured) {
    // Slide the take earlier by the latency — our own mic's, or for a take of
    // the partner's mic, the network + their device delay. If it would cross 0,
    // trim the head into the clip offset instead so nothing is lost.
    const comp = partnerTracks.has(trackId) && pComp ? pComp.total : localComp;
    let start = recordStartPos - comp, offset = 0;
    if (start < 0) { offset = Math.min(buf.duration, -start); start = 0; }
    const recEnd = start + (buf.duration - offset);
    // Overwrite punches over existing audio in the recorded region first.
    if (mode === "overwrite") store.overwriteRegion(trackId, start, recEnd);
    const asset = store.addAsset(buf, `Take ${new Date().toLocaleTimeString()}`);
    const clip = newClip(asset.id, asset, start);
    clip.offset = offset;
    clip.duration = buf.duration - offset;
    store.addClip(trackId, clip);
    // Comping stacks overlapping takes; Sound on Sound just layers (no change).
    if (mode === "comp") store.groupAsTake(trackId, clip.id);
    if (take && partnerTracks.has(trackId)) {
      const p = pendingPartnerTakes.get(take.takeId) ?? { anchor: take.anchor, clips: [] };
      p.clips.push({ clipId: clip.id, placed: { start: clip.start, offset: clip.offset, duration: clip.duration } });
      pendingPartnerTakes.set(take.takeId, p);
    }
  }
  if (take) remote.stopPartnerTake(take.takeId); // their app now sends its full-quality take
  // MIDI takes → MIDI clips on their instrument tracks
  let midiClips = 0;
  for (const [trackId, notes] of midiByTrack) {
    if (!notes.length) continue;
    const dur = Math.max(0.5, ...notes.map((n) => n.start + n.dur));
    const clip = newMidiClip(midiStartPos, dur, notes, `MIDI ${new Date().toLocaleTimeString()}`);
    store.addClip(trackId, clip);
    midiClips++;
  }
  recorder.disposeAll();
  lastArmSig = "";
  const takes = captured.size + midiClips;
  if (takes && pComp) toast(`Recorded ${takes} take(s). Partner take moved ${ms(pComp.total)} earlier to line up: ${compText(pComp)}.`, 10000);
  else if (takes) toast(`Recorded ${takes} take(s)${midiClips ? ` (${midiClips} MIDI)` : ""}.`);
  else toast("Nothing captured — play notes (🎹 Keys / MIDI) or check the mic meter on the armed track.", 6000);
}

// ---- files -----------------------------------------------------------
// Disk-backed project files (.dawproject). File System Access API where
// available (Save writes back to the same file); download/upload fallback.
let fileHandle: any = null; // FileSystemFileHandle
const FSA = "showSaveFilePicker" in window;
const DAW_TYPES = [{ description: "WebDAW project", accept: { "application/zip": [".dawproject"] } }];
const projFilename = () => sanitize(store.project.name) + ".dawproject";

function setNameFromFile(fname: string) {
  const base = fname.replace(/\.dawproject$/i, "");
  if (base) { store.project.name = base; projName.value = base; store.emitChange("project"); }
}

async function saveToFile(forceNew = false) {
  const blob = exportDawproject(store); // sync — must precede any await for the user-gesture
  try {
    if (FSA) {
      if (forceNew || !fileHandle) {
        fileHandle = await (window as any).showSaveFilePicker({ suggestedName: projFilename(), types: DAW_TYPES });
        setNameFromFile(fileHandle.name);
      }
      const w = await fileHandle.createWritable();
      await w.write(blob);
      await w.close();
      toast(`Saved ${fileHandle.name}`);
    } else {
      downloadBlob(blob, projFilename()); // fallback: lands in Downloads
      toast("Saved .dawproject to your downloads.");
    }
    // The user now has a project file → drop the browser quick-save fallback.
    void deleteProject(store.project.id).catch(() => {});
  } catch (e: any) {
    if (e?.name === "AbortError") return; // user canceled the picker
    console.error(e); toast("Save failed.");
  }
}

async function openFromFile() {
  try {
    if (FSA) {
      const [h] = await (window as any).showOpenFilePicker({ types: DAW_TYPES, multiple: false });
      fileHandle = h;
      const file = await h.getFile();
      await importDawproject(await file.arrayBuffer(), store, engine.ctx);
      setNameFromFile(h.name);
      tempoIn.value = String(store.project.tempo);
      toast(`Opened ${h.name}`);
    } else {
      pickFile(".dawproject,application/zip", importDaw);
    }
  } catch (e: any) {
    if (e?.name === "AbortError") return;
    console.error(e); toast("Could not open that file.");
  }
}

function openFileMenu() {
  const body = el("div", { class: "list" });
  body.append(
    menuItem("🆕 New project", () => { if (confirm("Discard current project?")) location.reload(); }),
    menuItem("📂 Open project file…", openFromFile),
    menuItem("💾 Save" + (FSA && fileHandle ? ` (${fileHandle.name})` : " project file…"), () => saveToFile(false)),
    menuItem("💾 Save As…", () => saveToFile(true)),
  );
  body.append(el("hr"));
  body.append(
    menuItem("☁️ Quick-save to browser", async () => { await saveProject(store); toast("Saved to this browser."); }),
    menuItem("🗂️ Open from browser…", openProjects),
  );
  if (!FSA) body.append(el("p", { class: "hint", text: "Your browser saves project files to Downloads and opens them via a file picker. (Chrome/Edge can save in place.)" }));
  openModal("File", body);
}

async function openProjects() {
  const items = await listProjects();
  const body = el("div", { class: "list" });
  if (!items.length) body.append(el("p", { class: "hint", text: "No saved projects yet." }));
  for (const p of items) {
    const item = el("div", { class: "item" }, [
      el("div", { class: "meta", html: `<b>${p.name}</b><br><small>${p.trackCount} tracks · ${new Date(p.modifiedAt).toLocaleString()}</small>` }),
    ]);
    const open = btn("Open", async () => { await loadProject(p.id, store, engine.ctx); projName.value = store.project.name; tempoIn.value = String(store.project.tempo); toast("Loaded."); document.querySelector(".modal-bg")?.remove(); });
    const del = btn("Delete", async () => { await deleteProject(p.id); item.remove(); });
    item.append(open, del);
    body.append(item);
  }
  openModal("Open project", body);
}

function openImportMenu() {
  const body = el("div", { class: "list" });
  body.append(
    menuItem("🎵 Import audio file (WAV/MP3/…)", () => pickFile("audio/*", importAudio)),
    menuItem("📦 Import .dawproject", () => pickFile(".dawproject,application/zip", importDaw)),
  );
  openModal("Import", body);
}

function openExportMenu() {
  const body = el("div", { class: "list" });
  body.append(
    menuItem("🌊 Export WAV (mixdown)", () => doExport("wav")),
    menuItem(`🎧 Export AAC ${aacSupported() ? "" : "(unsupported here)"}`, () => doExport("aac")),
    menuItem("📦 Export .dawproject", () => { const blob = exportDawproject(store); downloadBlob(blob, sanitize(store.project.name) + ".dawproject"); toast("Exported .dawproject"); }),
    menuItem("🎚️ Export session stems (.zip)", doExportStems),
  );
  openModal("Export", body);
}

async function doExportStems() {
  toast("Rendering stems…", 8000);
  try {
    const blob = await exportStems(store.project, store.buffers, 48000);
    downloadBlob(blob, sanitize(store.project.name) + "_stems.zip");
    toast("Exported session stems.");
  } catch (e: any) { console.error(e); toast(e?.message || "Stems export failed."); }
}

function menuItem(label: string, on: () => void) {
  const b = el("button", { text: label });
  b.style.textAlign = "left";
  b.addEventListener("click", () => { document.querySelector(".modal-bg")?.remove(); on(); });
  return b;
}

function pickFile(accept: string, cb: (file: File) => void) {
  const inp = el("input", { attrs: { type: "file", accept } }) as HTMLInputElement;
  inp.addEventListener("change", () => { if (inp.files?.[0]) cb(inp.files[0]); });
  inp.click();
}

async function importAudio(file: File, trackId?: string, startSec?: number) {
  const ab = await file.arrayBuffer();
  try {
    const buf = await engine.ctx.decodeAudioData(ab);
    const asset = store.addAsset(buf, file.name);
    let tid = trackId ?? store.selectedTrackId ?? store.project.tracks[0]?.id;
    if (!tid) { const t = newTrack(store.project.tracks.length, "audio"); store.addTrack(t); tid = t.id; }
    store.addClip(tid, newClip(asset.id, asset, startSec ?? engine.position()));
    toast(`Imported ${file.name}`);
  } catch { toast(`Could not decode ${file.name}.`); }
}

// Drop audio files onto the timeline → import at the dropped track + time. Drops
// onto an instrument track (or empty space) land on a new audio track.
async function dropAudioFiles(files: File[], trackId: string | null, startSec: number) {
  let tid = trackId && store.track(trackId)?.kind === "audio" ? trackId : null;
  if (!tid) { const t = newTrack(store.project.tracks.length, "audio"); store.addTrack(t); tid = t.id; }
  for (const f of files) await importAudio(f, tid, startSec);
}

async function importDaw(file: File) {
  try { await importDawproject(await file.arrayBuffer(), store, engine.ctx); projName.value = store.project.name; tempoIn.value = String(store.project.tempo); toast("Imported .dawproject"); }
  catch (e) { console.error(e); toast("Failed to import .dawproject"); }
}

async function doExport(kind: "wav" | "aac") {
  toast("Rendering mixdown…", 8000);
  try {
    const buf = await renderMixdown(store.project, store.buffers, { sampleRate: 48000 });
    if (kind === "wav") downloadBlob(encodeWav(buf), sanitize(store.project.name) + ".wav");
    else { const blob = await exportAac(buf); downloadBlob(blob, sanitize(store.project.name) + ".aac"); }
    toast(`Exported ${kind.toUpperCase()}.`);
  } catch (e: any) { console.error(e); toast(e?.message || "Export failed."); }
}

const sanitize = (s: string) => s.replace(/[^\w\-]+/g, "_") || "project";

// ---- effects picker --------------------------------------------------
function addFxPicker(target: string | "master") {
  const body = el("div", { class: "list" });
  for (const kind of ["eq", "compressor", "limiter", "lowpass", "highpass", "saturator", "chorus", "delay", "reverb", "width", "gain", "denoise", "deverb"] as const) {
    body.append(menuItem(EFFECT_LABELS[kind], () => {
      const fx = makeEffect(kind);
      store.addEffect(target, fx);
      openFxEditor(store, engine, target, fx.id, (id) => guiForWam(target, id)); // open its settings
    }));
  }
  body.append(el("hr"));
  for (const p of WAM_EFFECTS) body.append(menuItem(`🔌 ${p.name} (WAM)`, () => loadWam(target, p.url)));
  body.append(menuItem("🔌 Load WAM plugin by URL…", () => loadWamPrompt(target)));
  openModal("Insert effect", body);
}

function loadWamPrompt(target: string | "master") {
  const body = el("div");
  const inp = el("input", { attrs: { placeholder: "https://…/index.js (WAM v2 module)" } }) as HTMLInputElement;
  inp.style.width = "100%";
  body.append(el("p", { class: "hint", text: "Paste the URL of a Web Audio Module (WAM v2) plugin." }), inp);
  openModal("Load WAM plugin", body, [
    { label: "Load", primary: true, onClick: () => { if (inp.value) loadWam(target, inp.value); } },
  ]);
}

async function loadWam(target: string | "master", url: string) {
  toast("Loading plugin…");
  try {
    const fx: Effect = await loadWamEffect(engine, target, url);
    store.addEffect(target, fx);
    toast("Plugin loaded.");
    openFxEditor(store, engine, target, fx.id, (id) => guiForWam(target, id)); // open its settings
  } catch (e: any) { console.error(e); toast(e?.message || "Failed to load plugin (needs a WAM-compatible URL)."); }
}

function guiForWam(target: string | "master", fxId: string) {
  const list = target === "master" ? store.project.masterFx : store.track(target)?.fx;
  const fx = list?.find((f) => f.id === fxId);
  if (fx) openWamGui(fx.id);
}

// ---- remote ----------------------------------------------------------
function openRemoteMenu() {
  const body = el("div");
  if (remote.active) {
    body.append(el("p", { class: "hint", html: `In room <b>${remote.room ?? ""}</b> — status: <b>${syncBadge.textContent}</b>.<br>Leaving stops sharing your mic and ends sync. Your project stays as-is.` }));
    // Recording: local only, unless the partner allows it for this session.
    const rec = el("div", { class: "remote-rec" }, [el("b", { text: "Recording" })]);
    rec.append(el("p", { class: "hint", text: "Each of you records only your own mic/camera. Recording your partner needs their permission, which lasts for this session only." }));
    const g = remote.remoteGrant;
    if (g === "granted") {
      rec.append(el("p", { text: "✓ Your partner allowed you to record their mic this session. Pick “🌐 Partner's mic” as an audio track's input, arm it and record." }));
      // Latency compensation for partner takes: live estimate + manual fine-tune.
      const est = el("p", { class: "hint", text: "Measuring the delay of your partner's audio…" });
      const fine = el("input", { attrs: { type: "number", step: "5", title: "Fine-tune (ms): raise it if partner takes still land late, lower it if early" } }) as HTMLInputElement;
      fine.value = String(partnerOffsetMs); fine.style.width = "70px";
      const show = async () => {
        remote.requestLatency();
        await new Promise((r) => setTimeout(r, 250));
        const c = partnerCompensation(await remote.delayStats(), null);
        est.textContent = `Partner takes are moved ≈ ${ms(c.total)} earlier: ${compText(c)}. The jitter buffer is re-checked during every take.`;
      };
      const remeasure = el("button", { text: "↻ Re-measure" }) as HTMLButtonElement;
      remeasure.title = "Send a short chirp through your partner's audio to time the connection again (they won't hear it; you may)";
      remeasure.addEventListener("click", async () => {
        remeasure.disabled = true; est.textContent = "Measuring…";
        const d = await remote.measurePartnerPath();
        if (d == null) toast("Couldn't measure the audio path — using an estimate. Try again in a quieter moment.", 6000);
        remeasure.disabled = false; void show();
      });
      fine.addEventListener("change", () => { partnerOffsetMs = Math.round(Number(fine.value) || 0); fine.value = String(partnerOffsetMs); localStorage.setItem("webcut.partnerOffsetMs", String(partnerOffsetMs)); void show(); });
      rec.append(el("b", { class: "remote-lat-title", text: "Latency compensation" }), est,
        el("div", { class: "remote-lat-row" }, [el("span", { class: "hint", text: "Fine-tune (ms)" }), fine, remeasure]));
      void show();
    }
    else if (g === "pending") rec.append(el("p", { class: "hint", text: "⏳ Waiting for your partner to allow or deny recording…" }));
    else {
      if (g === "denied") rec.append(el("p", { class: "hint", text: "Your partner declined. You can ask again." }));
      const ask = el("button", { text: "🎙 Request to record partner's mic" }) as HTMLButtonElement;
      ask.disabled = !remote.connected;
      ask.addEventListener("click", () => { if (remote.requestRecording()) { toast("Asked your partner for permission to record their mic."); document.querySelector(".modal-bg")?.remove(); } });
      rec.append(ask);
    }
    if (remote.grantedToPeer) {
      const revoke = el("button", { text: "Stop allowing partner to record me" });
      revoke.addEventListener("click", () => { remote.setGrantToPeer(false); toast("Your partner can no longer record your mic."); document.querySelector(".modal-bg")?.remove(); });
      rec.append(el("p", { text: "Your partner may record your mic this session." }), revoke);
    }
    body.append(rec);
    openModal("Remote session", body, [
      { label: "Leave session", primary: true, onClick: () => { remote.disconnect(); toast("Left remote session."); } },
      { label: "Close", onClick: () => {} },
    ]);
    return;
  }
  body.append(el("p", { class: "hint", text: "Live remote session: share a room code with a collaborator to sync transport + project edits and stream live audio (WebRTC)." }));
  const room = el("input", { attrs: { placeholder: "Room code (e.g. studio-42)" } }) as HTMLInputElement;
  room.style.width = "100%";
  body.append(room);
  openModal("Remote session", body, [
    { label: "Host", onClick: () => { remote.connect(room.value || "room", true); } },
    { label: "Join", primary: true, onClick: () => { remote.connect(room.value || "room", false); } },
  ]);
}
remote.onStatus = (s) => { syncBadge.textContent = s; syncBadge.classList.toggle("live", s === "connected"); };

/** Run `fn` if the newest dialog is dismissed by clicking its backdrop. */
function onBackdropCancel(fn: () => void) {
  const all = document.querySelectorAll(".modal-bg");
  const bg = all[all.length - 1];
  bg?.addEventListener("mousedown", (e) => { if (e.target === bg) fn(); });
}

// ---- remote recording permission (per session) ----------------------
recorder.remoteStream = () => (remote.remoteGrant === "granted" ? remote.remoteStream() : null);
// Our own audio round trip, so the partner's recordings of us line up.
remote.localLatency = () => recordCompensation();

// Partner side: while they record us, record our own mic locally too (raw,
// exact timing) and send it to them when they stop.
const localTakes = new Map<string, { rec: NonNullable<Awaited<ReturnType<typeof createCapture>>>; stream: MediaStream }>();
remote.onPartnerTakeStart = async (takeId) => {
  try {
    const deviceId = (remote as any).localStream?.getAudioTracks()[0]?.getSettings().deviceId as string | undefined;
    const nr = recorder.processing === "nr";
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: deviceId ? { exact: deviceId } : undefined, echoCancellation: false, noiseSuppression: nr, autoGainControl: false } });
    const rec = await createCapture(engine.ctx, stream);
    if (!rec) { stream.getTracks().forEach((t) => t.stop()); return; }
    rec.begin();
    localTakes.set(takeId, { rec, stream });
  } catch (e) { console.warn("[webcut] local capture for partner failed", e); }
};
remote.onPartnerTakeStop = async (takeId) => {
  const t = localTakes.get(takeId);
  if (!t) return;
  localTakes.delete(takeId);
  const buf = await t.rec.finish();
  const startedAt = t.rec.startedAt;
  t.rec.dispose(); t.stream.getTracks().forEach((x) => x.stop());
  if (buf && startedAt != null) await remote.sendPartnerTake(takeId, buf, startedAt, recordCompensation());
};

// Our side: the partner's full-quality take arrived — put it exactly where it
// belongs and swap it in for the network recording.
remote.onPartnerTakeProgress = (_id, frac) => { if (frac < 1) toast(`Receiving your partner's full-quality take… ${Math.round(frac * 100)}%`, 2500); };
remote.onPartnerTake = (takeId, buf, startedAt, latency) => {
  const p = pendingPartnerTakes.get(takeId);
  pendingPartnerTakes.delete(takeId);
  if (!p || remote.clockOffset == null) return;
  // Their first sample in our audio clock → our timeline, minus their device latency.
  const exactStart = p.anchor.pos + (startedAt - remote.clockOffset - p.anchor.at) - latency;
  let start = exactStart, offset = 0;
  if (start < 0) { offset = -start; start = 0; }
  const asset = store.addAsset(buf, `Partner take ${new Date().toLocaleTimeString()} (full quality)`);
  let swapped = 0, kept = 0;
  store.pushUndo();
  for (const c of p.clips) {
    const f = store.findClip(c.clipId);
    if (!f) continue;
    const cl = f.clip;
    const untouched = Math.abs(cl.start - c.placed.start) < 1e-6 && Math.abs(cl.offset - c.placed.offset) < 1e-6 && Math.abs(cl.duration - c.placed.duration) < 1e-6;
    if (!untouched) { kept++; continue; } // edited meanwhile: leave it; the new take is in Media
    Object.assign(cl, { assetId: asset.id, start, offset, duration: Math.max(0.02, buf.duration - offset), name: asset.name });
    swapped++;
  }
  store.emitChange("tracks");
  const shift = p.clips.length ? Math.round((store.findClip(p.clips[0].clipId)?.clip.start ?? exactStart) * 1000 - p.clips[0].placed.start * 1000) : 0;
  toast(swapped
    ? `Your partner's full-quality take arrived and replaced the call recording (exact timing${shift ? `, ${shift > 0 ? "+" : ""}${shift} ms vs the call version` : ""}).`
    : kept ? "Your partner's full-quality take arrived — you'd already edited the call recording, so it was added to Media instead." : "Your partner's full-quality take arrived (added to Media).", 9000);
};
/** The partner asks to record our mic → explicit Allow / Deny (this session only). */
remote.onRecRequest = () => new Promise<boolean>((resolve) => {
  let settled = false;
  const done = (v: boolean) => { if (!settled) { settled = true; resolve(v); } };
  openModal("Allow your partner to record you?", el("div", {}, [
    el("p", { text: "Your partner wants to record your microphone during this session." }),
    el("p", { class: "hint", text: "This lasts only until the session ends — you'll be asked again next time. You'll see a banner whenever they're recording you, and you can stop allowing it at any time from ⇄ Remote." }),
  ]), [
    { label: "Deny", onClick: () => done(false) },
    { label: "Allow for this session", primary: true, onClick: () => done(true) },
  ]);
  onBackdropCancel(() => done(false));
});
let lastGrant = remote.remoteGrant;
remote.onRecPermission = () => {
  const g = remote.remoteGrant;
  if (g !== lastGrant) {
    if (g === "granted") {
      toast("Your partner allowed recording their mic this session — choose “🌐 Partner's mic” as a track input.", 7000);
      void (async () => { // time their audio path so takes line up
        for (let i = 0; i < 30 && remote.clockOffset == null; i++) await new Promise((r) => setTimeout(r, 200));
        if (remote.remoteGrant === "granted") await remote.measurePartnerPath();
      })();
    }
    else if (g === "denied") toast("Your partner declined to be recorded.", 5000);
    else if (lastGrant === "granted") {
      toast("Recording your partner is no longer allowed (they revoked it or the session ended).", 7000);
      if (engine.recording && recordingPartner) void finishRecord(); // keep what was recorded until now
      // Tracks listening to the partner fall back to the default input.
      for (const t of store.project.tracks) if (t.inputDeviceId === REMOTE_INPUT) store.updateTrack(t.id, { inputDeviceId: null }, "tracks");
    }
    lastGrant = g;
  }
  pushDevices();
};
// A banner whenever the partner is recording OUR mic.
const peerRecBanner = el("div", { class: "rec-banner peer-rec-banner" }, [
  el("span", { class: "rec-dot" }), el("b", { text: "Your partner is recording your microphone" }),
]);
const stopAllowBtn = el("button", { class: "peer-rec-stop", text: "Stop allowing" });
stopAllowBtn.addEventListener("click", () => { remote.setGrantToPeer(false); toast("Your partner can no longer record your mic."); });
peerRecBanner.append(stopAllowBtn);
peerRecBanner.style.display = "none";
document.body.append(peerRecBanner);
remote.onPeerRecording = (on) => { peerRecBanner.style.display = on ? "flex" : "none"; };


// ---- render loop -----------------------------------------------------
let lastTimecode = "";
function tick() {
  timeline.draw();
  const lvls = engine.levels();
  mixer.updateMeters(lvls);
  // merge recorder input levels into head meters
  const recLvls = recorder.levels();
  const headLvls: Record<string, number> = { ...lvls.tracks };
  for (const k in recLvls) headLvls[k] = Math.max(headLvls[k] ?? 0, recLvls[k]);
  timeline.updateMeters(headLvls);
  const pos = engine.position();
  const tc = `${fmtTime(pos)}<div class="bars">${fmtBars(pos, store.project.tempo, store.project.beatsPerBar)}</div>`;
  if (tc !== lastTimecode) { timecode.innerHTML = tc; lastTimecode = tc; }
  timeline.followPlayhead(pos, engine.playing);
  const zv = String(Math.round(timeline.pps));
  if (document.activeElement !== zoomSlider && zoomSlider.value !== zv) zoomSlider.value = zv;
  // live recording waveform overlay (placed at the latency-compensated start so it
  // doesn't jump when the take finalizes)
  if (engine.recording) {
    const start = Math.max(0, recordStartPos - recordCompensation());
    const partnerStart = Math.max(0, recordStartPos - (lastPartnerComp?.total ?? partnerCompensation(null).total));
    const tracks: Record<string, any> = {};
    for (const [id, pk] of recorder.recordingPeaks()) tracks[id] = { startSec: partnerTrackIds.has(id) ? partnerStart : start, min: pk.min, max: pk.max, binSamples: pk.binSamples };
    timeline.setRecordOverlay({ sampleRate: engine.ctx.sampleRate, tracks });
  } else {
    timeline.setRecordOverlay(null);
  }
  requestAnimationFrame(tick);
}
requestAnimationFrame(tick);

engine.onTransport = () => { tPlay.textContent = engine.playing ? "⏸" : "▶"; };
store.on("change", ({ scope }) => {
  if (scope === "tracks" || scope === "project") refreshArm();
  if (scope === "tracks" || scope === "mixer") for (const t of store.project.tracks) recorder.setInputGain(t.id, t.inputGain ?? 0); // live input-gain
  if (scope === "mixer" || scope === "project" || scope === "tracks") {
    const ids = new Set<string>();
    for (const t of store.project.tracks) for (const f of t.fx) ids.add(f.id);
    for (const f of store.project.masterFx) ids.add(f.id);
    reconcileWam(engine, ids);
  }
  if (scope === "project") { projName.value = store.project.name; tempoIn.value = String(store.project.tempo); recModeSel.value = store.project.recordMode ?? "comp"; }
  if (scope === "project" || scope === "transport") loopBtn.classList.toggle("on", store.project.loopEnabled);
});

// ---- keyboard --------------------------------------------------------
window.addEventListener("keydown", (e) => {
  const tag = (e.target as HTMLElement)?.tagName;
  if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
  if (e.code === "Space") { e.preventDefault(); togglePlay(); }
  else if (e.key === "r" || e.key === "R") { e.preventDefault(); toggleRecord(); }
  else if (e.key === "Delete" || e.key === "Backspace") { if (store.selectedClipIds.size) store.removeClips(); }
  else if ((e.key === "s" || e.key === "S") && !e.ctrlKey && !e.metaKey) { if (store.selectedClipIds.size) store.splitClipsAt(engine.position()); }
  else if ((e.ctrlKey || e.metaKey) && e.key === "d") { e.preventDefault(); if (store.selectedClipIds.size) store.duplicateClips(); }
  else if ((e.ctrlKey || e.metaKey) && e.key === "a") { e.preventDefault(); store.setClipSelection(store.project.tracks.flatMap((t) => t.clips.filter((c) => c.takeActive !== false).map((c) => c.id))); }
  else if ((e.ctrlKey || e.metaKey) && e.key === "z") { e.preventDefault(); store.undo(); }
  else if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.shiftKey && e.key === "Z"))) { e.preventDefault(); store.redo(); }
  else if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "s" || e.key === "S")) { e.preventDefault(); saveToFile(true); }
  else if ((e.ctrlKey || e.metaKey) && e.key === "s") { e.preventDefault(); saveToFile(false); }
  else if ((e.ctrlKey || e.metaKey) && e.key === "o") { e.preventDefault(); openFromFile(); }
  else if (e.key === "Home") engine.seek(0);
});

// ---- boot ------------------------------------------------------------
(async () => {
  // Register the real-time denoise worklet so the "Denoise (profile)" insert can
  // be created synchronously when added to a track.
  try { await engine.ctx.audioWorklet.addModule(DENOISE_WORKLET_URL); } catch (e) { console.warn("denoise worklet unavailable", e); }
  // Same for the "Deverb (room echo)" insert.
  try { await engine.ctx.audioWorklet.addModule(DEVERB_WORKLET_URL); } catch (e) { console.warn("deverb worklet unavailable", e); }
  try { devices = await listInputs(); pushDevices(); } catch {}
  try { mixer.setOutputs(await listOutputs()); } catch {}
})();

// We do NOT register a service worker (sw.js is now a kill-switch that cleans up
// any stale install from earlier versions). Proactively unregister + clear caches
// so a previously-cached worker can't serve stale code and block boot.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations?.().then((rs) => rs.forEach((r) => r.unregister())).catch(() => {});
  (globalThis as any).caches?.keys?.().then((ks: string[]) => ks.forEach((k) => caches.delete(k))).catch(() => {});
}

// Request hardware MIDI access once instrument tracks are present (added now, or
// in a project that was loaded/opened) — never on a plain audio-only session.
store.on("change", ({ scope }) => {
  if ((scope === "tracks" || scope === "project") && store.project.tracks.some((t) => t.kind === "instrument")) midi.ensureWebMidi();
});

// Auto quick-save to the browser every ~10 min as a safety net — only while the
// project has no disk file (a saved file is the source of truth). Same project
// id, so each save overwrites the previous one.
const AUTOSAVE_MS = 10 * 60 * 1000;
setInterval(() => {
  if (fileHandle) return;
  const p = store.project;
  if (!p.tracks.some((t) => t.clips.length) && !p.assets.length) return; // nothing worth saving yet
  saveProject(store).then(() => toast("Auto-saved to browser.", 1500)).catch(() => {});
}, AUTOSAVE_MS);

// resume audio on first gesture (autoplay policy)
window.addEventListener("pointerdown", () => engine.resume(), { once: true });

(window as any).webdaw = { store, engine, remote, timeline, recorder, midi };
